/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Ergonomic Font Scale Selector for Admin Portal
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { useTheme, type FontScale } from '../../context/ThemeProvider';

export interface FontScaleSelectorProps {
  className?: string;
}

const SCALE_OPTIONS: { id: FontScale; label: string; scale: string; letterSizeClass: string }[] = [
  { id: 'standard', label: 'Standard', scale: '100%', letterSizeClass: 'text-xs' },
  { id: 'comfortable', label: 'Comfortable', scale: '110%', letterSizeClass: 'text-sm font-semibold' },
  { id: 'large', label: 'Large', scale: '120%', letterSizeClass: 'text-base font-bold' },
];

export const FontScaleSelector: React.FC<FontScaleSelectorProps> = ({ className = '' }) => {
  const { fontScale, setFontScale } = useTheme();

  return (
    <div
      className={`inline-flex items-center p-0.5 rounded-lg border border-surface-border bg-surface ${className}`}
      role="group"
      aria-label="Text Size Preference"
    >
      {SCALE_OPTIONS.map((opt) => {
        const isActive = fontScale === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => setFontScale(opt.id)}
            className={`px-2 py-1 rounded text-xs transition-all cursor-pointer ${
              isActive
                ? 'bg-primary text-white font-semibold shadow-xs'
                : 'text-foreground-muted hover:text-foreground hover:bg-muted/60'
            }`}
            title={`${opt.label} text size (${opt.scale})`}
            aria-label={`${opt.label} text size (${opt.scale})`}
            aria-pressed={isActive}
          >
            <span className={opt.letterSizeClass}>A</span>
          </button>
        );
      })}
    </div>
  );
};

